import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const targetUserId = userId || currentUser?.id;
      const response = await api.get(`/profile/${targetUserId}`);
      setProfileData(response.data);
      setIsOwnProfile(!userId || currentUser?.id === targetUserId);
    } catch (err) {
      console.error('Error fetching profile:', err);
      if (err.response?.status === 403) {
        setError('You can only view profiles of people you\'ve worked with');
      } else {
        setError('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-xl text-gray-600">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <ProfileHeader
          profileData={profileData}
          isOwnProfile={isOwnProfile}
          onUpdate={fetchProfile}
        />

        <StatsSection userId={userId || currentUser?.id} />

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('tasks')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'tasks'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Task History
              </button>
              <button
                onClick={() => setActiveTab('links')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'links'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Links
              </button>
              <button
                onClick={() => setActiveTab('qa')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'qa'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Q&A
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'tasks' && (
              <TaskHistoryTimeline
                userId={userId || currentUser?.id}
                isOwnProfile={isOwnProfile}
              />
            )}

            {activeTab === 'links' && (
              <LinksSection
                userId={userId || currentUser?.id}
                isOwnProfile={isOwnProfile}
                links={profileData?.links || []}
                onUpdate={fetchProfile}
              />
            )}

            {activeTab === 'qa' && (
              <QASection
                userId={userId || currentUser?.id}
                isOwnProfile={isOwnProfile}
                qaResponses={profileData?.qa_responses || []}
                onUpdate={fetchProfile}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Header Component
const ProfileHeader = ({ profileData, isOwnProfile, onUpdate }) => {
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bio, setBio] = useState(profileData?.bio || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState(profileData?.first_name || '');
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [location, setLocation] = useState(profileData?.location || '');

  const handleSaveBio = async () => {
    try {
      await api.put('/profile/bio', { bio });
      setIsEditingBio(false);
      onUpdate();
    } catch (err) {
      console.error('Error saving bio:', err);
      alert('Failed to save bio');
    }
  };

  const handleSaveName = async () => {
    if (!name.trim()) {
      alert('Name is required');
      return;
    }

    try {
      await api.put('/profile/name', { first_name: name.trim() });
      setIsEditingName(false);
      onUpdate();
    } catch (err) {
      console.error('Error saving name:', err);
      alert(err.response?.data?.error || 'Failed to save name');
    }
  };

  const handleSaveLocation = async () => {
    try {
      await api.put('/profile/location', { location: location.trim() });
      setIsEditingLocation(false);
      onUpdate();
    } catch (err) {
      console.error('Error saving location:', err);
      alert(err.response?.data?.error || 'Failed to save location');
    }
  };

  // Photo upload disabled for MVP
  // const handlePhotoUpload = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;

  //   const formData = new FormData();
  //   formData.append('photo', file);

  //   try {
  //     await api.post('/profile/photo', formData, {
  //       headers: { 'Content-Type': 'multipart/form-data' }
  //     });
  //     onUpdate();
  //   } catch (err) {
  //     console.error('Error uploading photo:', err);
  //     alert('Failed to upload photo');
  //   }
  // };

  return (
    <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
      <div className="text-center">
        {/* Profile photos hidden for MVP */}

        {/* Name */}
        {!isEditingName ? (
          <h1
            onClick={() => isOwnProfile && setIsEditingName(true)}
            className={`text-3xl font-bold text-gray-900 mb-2 ${isOwnProfile ? 'cursor-pointer hover:bg-gray-50 inline-block px-3 py-1 rounded transition-colors' : ''}`}
          >
            {name || profileData?.first_name}
          </h1>
        ) : (
          <div className="mb-2 inline-block">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
              className="text-3xl font-bold text-gray-900 text-center border-b-2 border-blue-500 focus:outline-none px-2"
              placeholder="Your name"
              autoFocus
            />
            <div className="flex justify-center gap-2 mt-2">
              <button
                onClick={() => {
                  setName(profileData?.first_name || '');
                  setIsEditingName(false);
                }}
                className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveName}
                className="px-3 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* Location */}
        {!isEditingLocation ? (
          <div
            onClick={() => isOwnProfile && setIsEditingLocation(true)}
            className={`text-gray-600 mb-4 ${isOwnProfile ? 'cursor-pointer hover:bg-gray-50 inline-block px-3 py-1 rounded transition-colors' : ''}`}
          >
            {location || profileData?.location ? (
              <p>📍 {location || profileData?.location}</p>
            ) : (
              isOwnProfile && <p className="text-gray-400 italic">Click to add location</p>
            )}
          </div>
        ) : (
          <div className="mb-4">
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              maxLength={100}
              className="text-gray-600 text-center border-b-2 border-blue-500 focus:outline-none px-2"
              placeholder="Add your location"
              autoFocus
            />
            <div className="flex justify-center gap-2 mt-2">
              <button
                onClick={() => {
                  setLocation(profileData?.location || '');
                  setIsEditingLocation(false);
                }}
                className="px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLocation}
                className="px-3 py-1 text-xs bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* Bio */}
        <div className="max-w-2xl mx-auto mt-6">
          {!isEditingBio ? (
            <div
              onClick={() => isOwnProfile && setIsEditingBio(true)}
              className={`text-gray-700 min-h-[60px] flex items-center justify-center ${isOwnProfile ? 'cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors' : ''}`}
            >
              {bio || (isOwnProfile ? <span className="text-gray-400 italic">Click to add a bio</span> : <span className="text-gray-400 italic">No bio yet</span>)}
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                maxLength={500}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="4"
                placeholder="Tell people about yourself..."
                autoFocus
              />
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">{bio.length}/500</span>
                <div className="space-x-2">
                  <button
                    onClick={() => {
                      setBio(profileData?.bio || '');
                      setIsEditingBio(false);
                    }}
                    className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveBio}
                    className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Stats Section Component
const StatsSection = ({ userId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/profile/stats/${userId}`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="animate-pulse text-gray-400">Loading stats...</div>
      </div>
    );
  }

  if (!stats) return null;

  const displayMinutes = stats.totalMinutes > 120
    ? `${(stats.totalMinutes / 60).toFixed(1)} Hours`
    : `${stats.totalMinutes} Minutes`;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Stats</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="text-4xl font-bold text-blue-600 mb-1">{stats.totalSessions}</div>
          <div className="text-gray-600 text-sm">Total Sessions</div>
        </div>
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <div className="text-4xl font-bold text-purple-600 mb-1">{displayMinutes}</div>
          <div className="text-gray-600 text-sm">Total Time</div>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-sm text-gray-700 font-medium min-h-[40px] flex items-center justify-center">
            {stats.keywords && stats.keywords.length > 0
              ? stats.keywords.map(k => `#${k}`).join(' ')
              : 'No sessions yet'}
          </div>
          <div className="text-gray-600 text-sm mt-1">Top Keywords</div>
        </div>
      </div>
    </div>
  );
};

// Links Section Component
const LinksSection = ({ userId, isOwnProfile, links, onUpdate }) => {
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLink, setNewLink] = useState({ type: 'website', label: '', url: '' });
  // const [editingLinkId, setEditingLinkId] = useState(null);

  const linkTypes = [
    { value: 'portfolio', label: 'Portfolio', icon: '🎨' },
    { value: 'instagram', label: 'Instagram', icon: '📷' },
    { value: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { value: 'website', label: 'Website', icon: '🌐' },
    { value: 'custom', label: 'Custom', icon: '🔗' }
  ];

  const handleAddLink = async () => {
    if (!newLink.label || !newLink.url) {
      alert('Please fill in all fields');
      return;
    }

    // Ensure URL has a protocol (http:// or https://)
    let formattedUrl = newLink.url.trim();
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }

    try {
      await api.post(`/profile/${userId}/links`, {
        ...newLink,
        url: formattedUrl
      });
      setNewLink({ type: 'website', label: '', url: '' });
      setShowAddLink(false);
      onUpdate();
    } catch (err) {
      console.error('Error adding link:', err);
      alert('Failed to add link');
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Delete this link?')) return;

    try {
      await api.delete(`/profile/links/${linkId}`);
      onUpdate();
    } catch (err) {
      console.error('Error deleting link:', err);
      alert('Failed to delete link');
    }
  };

  const getLinkIcon = (type) => {
    return linkTypes.find(t => t.value === type)?.icon || '🔗';
  };

  const formatUrl = (url) => {
    // Ensure URL has a protocol for external links to work properly
    if (!url) return '';
    const trimmedUrl = url.trim();
    if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      return 'https://' + trimmedUrl;
    }
    return trimmedUrl;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900">Links</h2>
        {isOwnProfile && links.length < 10 && (
          <button
            onClick={() => setShowAddLink(true)}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Add Link
          </button>
        )}
      </div>

      {/* Add Link Form */}
      {showAddLink && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={newLink.type}
                onChange={(e) => setNewLink({...newLink, type: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {linkTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.icon} {type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
              <input
                type="text"
                value={newLink.label}
                onChange={(e) => setNewLink({...newLink, label: e.target.value})}
                placeholder="My Portfolio"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
              <input
                type="url"
                value={newLink.url}
                onChange={(e) => setNewLink({...newLink, url: e.target.value})}
                placeholder="https://..."
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowAddLink(false);
                  setNewLink({ type: 'website', label: '', url: '' });
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLink}
                className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm"
              >
                Add Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Links Display */}
      {links && links.length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {links.map(link => (
            <div key={link.id} className="relative group">
              <a
                href={formatUrl(link.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors"
              >
                <span>{getLinkIcon(link.type)}</span>
                <span className="font-medium">{link.label}</span>
              </a>
              {isOwnProfile && (
                <button
                  onClick={() => handleDeleteLink(link.id)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-4">
          {isOwnProfile ? 'Add links to help people connect with you' : 'No links added yet'}
        </p>
      )}
    </div>
  );
};

// Q&A Section Component
const QASection = ({ userId, isOwnProfile, qaResponses, onUpdate }) => {
  const defaultQuestions = [
    "What is your favorite book?",
    "What do you do to reset?",
    "What's your go-to productivity hack?",
    "What are you currently learning?",
    "What's your favorite way to start the day?"
  ];

  const [editingQuestion, setEditingQuestion] = useState(null);
  const [answers, setAnswers] = useState(
    defaultQuestions.reduce((acc, q) => {
      const existing = qaResponses?.find(r => r.question === q);
      acc[q] = existing?.answer || '';
      return acc;
    }, {})
  );

  const handleSaveAnswer = async (question) => {
    const answer = answers[question];  // eslint-disable-line no-unused-vars

    try {
      const updatedResponses = defaultQuestions.map(q => ({
        question: q,
        answer: answers[q] || ''
      })).filter(r => r.answer); // Only save answered questions

      await api.put('/profile/qa', { qaResponses: updatedResponses });
      setEditingQuestion(null);
      onUpdate();
    } catch (err) {
      console.error('Error saving answer:', err);
      alert('Failed to save answer');
    }
  };

  const answeredQuestions = defaultQuestions.filter(q => answers[q]);
  const hasAnswers = answeredQuestions.length > 0;

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Q&A</h2>

      {!hasAnswers && !isOwnProfile ? (
        <p className="text-gray-500 text-center py-4">No questions answered yet</p>
      ) : !hasAnswers && isOwnProfile ? (
        <p className="text-gray-500 text-center py-4">Answer questions to help others get to know you</p>
      ) : null}

      <div className="space-y-4">
        {defaultQuestions.map(question => {
          const hasAnswer = answers[question];
          if (!hasAnswer && !isOwnProfile) return null;

          return (
            <div key={question} className="border-b border-gray-200 pb-4 last:border-0">
              <h3 className="font-semibold text-gray-900 mb-2">{question}</h3>

              {editingQuestion === question ? (
                <div className="space-y-2">
                  <textarea
                    value={answers[question]}
                    onChange={(e) => setAnswers({...answers, [question]: e.target.value})}
                    maxLength={500}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Your answer..."
                    autoFocus
                  />
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">{answers[question]?.length || 0}/500</span>
                    <div className="space-x-2">
                      <button
                        onClick={() => setEditingQuestion(null)}
                        className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleSaveAnswer(question)}
                        className="px-4 py-2 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => isOwnProfile && setEditingQuestion(question)}
                  className={`text-gray-700 ${isOwnProfile ? 'cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors' : ''}`}
                >
                  {hasAnswer ? (
                    <p>{answers[question]}</p>
                  ) : (
                    isOwnProfile && <p className="text-gray-400 italic">Click to answer</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Task History Timeline Component
const TaskHistoryTimeline = ({ userId, isOwnProfile }) => {
  const [tasks, setTasks] = useState([]);
  const [hiddenTasks, setHiddenTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    categories: [],
    startDate: '',
    endDate: ''
  });
  const [showHidden, setShowHidden] = useState(false);

  // const categories = ['general_productivity', 'creative', 'spiritual', 'startup'];

  const categoryColors = {
    general_productivity: 'bg-blue-100 text-blue-800',
    creative: 'bg-purple-100 text-purple-800',
    spiritual: 'bg-green-100 text-green-800',
    startup: 'bg-orange-100 text-orange-800'
  };

  const fetchTasks = async (reset = false) => {
    try {
      setLoading(true);
      const currentPage = reset ? 0 : page;
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...filters,
        categories: filters.categories.join(',')
      });

      const response = await api.get(`/profile/${userId}/tasks?${params}`);

      if (reset) {
        setTasks(response.data.tasks);
        setHiddenTasks(response.data.hiddenTasks || []);
      } else {
        setTasks([...tasks, ...response.data.tasks]);
      }

      setHasMore(response.data.hasMore);
      setPage(currentPage + 1);
    } catch (err) {
      console.error('Error fetching tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, filters]);

  const handleTogglePrivacy = async (taskId, currentIsHidden) => {
    try {
      await api.put(`/profile/tasks/${taskId}/visibility`, {
        isHidden: !currentIsHidden
      });
      fetchTasks(true);
    } catch (err) {
      console.error('Error toggling task privacy:', err);
      alert('Failed to update task visibility');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure? This cannot be undone.')) return;

    try {
      await api.delete(`/profile/tasks/${taskId}`);
      fetchTasks(true);
      alert('Task deleted');
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCategory = (category) => {
    return category.replace('_', ' ').split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-4">Task History</h2>

      {/* Filters */}
      <div className="mb-6 pb-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {(filters.categories.length > 0 || filters.startDate || filters.endDate) && (
            <button
              onClick={() => setFilters({ categories: [], startDate: '', endDate: '' })}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Task List */}
        {loading && page === 0 ? (
          <div className="text-center py-8 text-gray-500">Loading tasks...</div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-xl text-gray-500 mb-2">✨ Being a better me everyday ✨</p>
            <p className="text-gray-400">
              {isOwnProfile
                ? "Your journey starts here. Complete your first session to see your progress!"
                : "No tasks found. Try adjusting your filters."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {tasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                isOwnProfile={isOwnProfile}
                categoryColors={categoryColors}
                formatCategory={formatCategory}
                formatDate={formatDate}
                onTogglePrivacy={handleTogglePrivacy}
                onDelete={handleDeleteTask}
              />
            ))}
          </div>
        )}

        {/* Load More */}
        {hasMore && tasks.length > 0 && (
          <div className="text-center mt-6">
            <button
              onClick={() => fetchTasks(false)}
              disabled={loading}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

        {!hasMore && tasks.length > 0 && (
          <p className="text-center text-gray-500 mt-6">You've reached the end</p>
        )}

      {/* Hidden Tasks Section */}
      {isOwnProfile && hiddenTasks.length > 0 && (
        <div className="mt-6 border-t border-gray-200 pt-6">
          <button
            onClick={() => setShowHidden(!showHidden)}
            className="w-full flex justify-between items-center text-left"
          >
            <h2 className="text-xl font-bold text-gray-900">
              Hidden Tasks ({hiddenTasks.length})
            </h2>
            <span className="text-gray-500">{showHidden ? '▼' : '▶'}</span>
          </button>

          {showHidden && (
            <div className="mt-4 space-y-4">
              {hiddenTasks.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  isOwnProfile={true}
                  categoryColors={categoryColors}
                  formatCategory={formatCategory}
                  formatDate={formatDate}
                  onTogglePrivacy={handleTogglePrivacy}
                  onDelete={handleDeleteTask}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, isOwnProfile, categoryColors, formatCategory, formatDate, onTogglePrivacy, onDelete }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${categoryColors[task.category] || 'bg-gray-100 text-gray-800'}`}>
          {formatCategory(task.category)}
        </span>
        {task.isHidden && isOwnProfile && (
          <span className="text-gray-400" title="Private task">🔒</span>
        )}
      </div>

      <p className="text-gray-800 mb-4 whitespace-pre-wrap">{task.description}</p>

      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 mb-4">
        <span>📅 {formatDate(task.createdAt)}</span>
        <span>•</span>
        <span>{task.durationMinutes} minutes</span>
        {task.partner && (
          <>
            <span>•</span>
            <span>
              👤 Worked with{' '}
              <a
                href={`/profile/${task.partner.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                {task.partner.name}
              </a>
            </span>
          </>
        )}
      </div>

      {isOwnProfile && (
        <div className="flex gap-2">
          <button
            onClick={() => onTogglePrivacy(task.id, task.isHidden)}
            className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {task.isHidden ? 'Make Public' : 'Make Private'}
          </button>
          <button
            onClick={() => onDelete(task.id)}
            className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default Profile;
